import { ERROR_CATALOG, getCatalogEntry } from "./catalog";
import { generateCorrelationId } from "./correlationId";
import type { AppError, ErrorContext, StructuredBackendError } from "./types";

interface PatternMatch {
  pattern: RegExp | string;
  errorCode: string;
}

const BACKEND_STRING_PATTERNS: PatternMatch[] = [
  { pattern: "Invalid login credentials", errorCode: "AUTH-002" },
  { pattern: "invalid_credentials", errorCode: "AUTH-002" },
  { pattern: "Email not confirmed", errorCode: "AUTH-001" },
  { pattern: "JWT", errorCode: "AUTH-001" },
  { pattern: /session.*expir/i, errorCode: "AUTH-001" },
  { pattern: "row-level security", errorCode: "AUTH-004" },
  { pattern: "Unauthorized", errorCode: "AUTH-001" },
  { pattern: "Not authenticated", errorCode: "AUTH-001" },
  { pattern: "Only organization admins can cancel", errorCode: "SUB-003" },
  { pattern: "not_found_or_already_cancelled", errorCode: "SUB-002" },
  { pattern: "Cancellation failed", errorCode: "SUB-006" },
  { pattern: "Target plan is not a downgrade", errorCode: "SUB-007" },
  { pattern: "Missing downgrade_plan_id", errorCode: "SUB-007" },
  { pattern: "Forbidden", errorCode: "TEAM-001" },
  { pattern: /permission denied/i, errorCode: "TEAM-001" },
  { pattern: "Access denied", errorCode: "AUTH-004" },
  { pattern: /interview.*not found/i, errorCode: "INT-001" },
  { pattern: /interview.*cancel/i, errorCode: "INT-003" },
  { pattern: /interview.*expir/i, errorCode: "INT-002" },
  { pattern: /interview.*completed/i, errorCode: "INT-004" },
  { pattern: /invalid.*token/i, errorCode: "INT-005" },
  { pattern: /interview.*already in progress/i, errorCode: "INT-010" },
  { pattern: /scoring.*already in progress/i, errorCode: "INT-010" },
  { pattern: /already being scored/i, errorCode: "INT-010" },
  { pattern: /already being generated/i, errorCode: "INT-010" },
  { pattern: /unsupported.*file/i, errorCode: "CV-001" },
  { pattern: /file.*too large/i, errorCode: "CV-002" },
  { pattern: /upload.*fail/i, errorCode: "CV-003" },
  { pattern: /pars.*fail/i, errorCode: "CV-005" },
  { pattern: /scoring.*fail/i, errorCode: "CV-006" },
  { pattern: /cv.*already in progress/i, errorCode: "CV-008" },
  { pattern: /parsing.*already in progress/i, errorCode: "CV-008" },
  { pattern: /merge fail/i, errorCode: "MERGE-001" },
  { pattern: /ai.*unavailable/i, errorCode: "AIINT-001" },
  { pattern: /ai.*timeout/i, errorCode: "AIINT-002" },
  { pattern: /ServiceNotAvailableError/i, errorCode: "AIINT-001" },
  { pattern: /EntitlementError/i, errorCode: "SUB-006" },
  { pattern: /already mapped to this plan/i, errorCode: "PSVC-001" },
  { pattern: /platform mismatch/i, errorCode: "PSVC-002" },
  { pattern: /publish.*block/i, errorCode: "PSVC-003" },
  { pattern: /already applied/i, errorCode: "APP-002" },
  { pattern: /already.*withdrawn/i, errorCode: "APP-003" },
  { pattern: /stage advance fail/i, errorCode: "JOB-006" },
  { pattern: /offer.*not found/i, errorCode: "OFR-001" },
  { pattern: /offer.*expir/i, errorCode: "OFR-002" },
  { pattern: /offer.*no longer actionable/i, errorCode: "OFR-003" },
  { pattern: "vat_invalid", errorCode: "BILL-IDENT-001" },
  { pattern: "alt_id_pair_incomplete", errorCode: "BILL-IDENT-002" },
  { pattern: "alt_id_scheme_invalid", errorCode: "BILL-IDENT-003" },
  { pattern: "alt_id_value_invalid", errorCode: "BILL-IDENT-004" },
  { pattern: "alt_id_scheme_required", errorCode: "BILL-IDENT-005" },
  { pattern: "alt_id_value_required", errorCode: "BILL-IDENT-005" },
  { pattern: "legal_name_required", errorCode: "BILL-IDENT-006" },
  { pattern: "legal_name_invalid", errorCode: "BILL-IDENT-006" },
  { pattern: "country_invalid", errorCode: "BILL-IDENT-007" },
  { pattern: "postal_zone_invalid", errorCode: "BILL-IDENT-008" },
  { pattern: "postal_zone_required", errorCode: "BILL-IDENT-008" },
  { pattern: "building_number_invalid", errorCode: "BILL-IDENT-009" },
  { pattern: "building_number_required", errorCode: "BILL-IDENT-009" },
  { pattern: "plot_identification_invalid", errorCode: "BILL-IDENT-010" },
  { pattern: "plot_identification_required", errorCode: "BILL-IDENT-010" },
  { pattern: "street_invalid", errorCode: "BILL-IDENT-011" },
  { pattern: "street_required", errorCode: "BILL-IDENT-011" },
  { pattern: "city_invalid", errorCode: "BILL-IDENT-012" },
  { pattern: "city_required", errorCode: "BILL-IDENT-012" },
  { pattern: "district_invalid", errorCode: "BILL-IDENT-013" },
  { pattern: "district_required", errorCode: "BILL-IDENT-013" },
  { pattern: "region_required", errorCode: "BILL-IDENT-014" },
  { pattern: "region_invalid", errorCode: "BILL-IDENT-014" },
  { pattern: "buyer_alt_id_scheme_not_business", errorCode: "BILL-IDENT-015" },
  { pattern: "b2b_identity_incomplete", errorCode: "BILL-IDENT-016" },
  { pattern: "no_billing_contact", errorCode: "BILL-IDENT-017" },
  { pattern: "billing_contact_role_invalid", errorCode: "BILL-IDENT-018" },
  { pattern: "PAYMENT_SETUP_NOT_READY", errorCode: "BILL-PAY-001" },
  { pattern: /checkout configuration is incomplete/i, errorCode: "BILL-PAY-001" },
  { pattern: "23505", errorCode: "TEAM-002" },
  { pattern: "42501", errorCode: "AUTH-004" },
  { pattern: "23503", errorCode: "SYS-001" },
  { pattern: "PGRST116", errorCode: "SYS-001" },
];

function isStructuredBackendError(obj: unknown): obj is StructuredBackendError {
  if (!obj || typeof obj !== "object") return false;
  const value = obj as Record<string, unknown>;
  return typeof value.error_code === "string" && typeof value.user_title === "string";
}

function extractRawMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object") {
    const value = error as Record<string, unknown>;
    if (typeof value.message === "string") return value.message;
    if (typeof value.error === "string") return value.error;
    if (typeof value.error_description === "string") return value.error_description;
    if (value.error && typeof value.error === "object") {
      const nested = value.error as Record<string, unknown>;
      if (typeof nested.message === "string") return nested.message;
    }
  }
  return "";
}

function isNetworkError(error: unknown): boolean {
  const message = extractRawMessage(error).toLowerCase();
  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("network request failed") ||
    message.includes("load failed") ||
    message.includes("cors") ||
    (typeof navigator !== "undefined" && !navigator.onLine)
  );
}

function isTimeoutError(error: unknown): boolean {
  const message = extractRawMessage(error).toLowerCase();
  return (
    message.includes("timeout") ||
    message.includes("timed out") ||
    message.includes("aborted") ||
    (error instanceof DOMException && error.name === "AbortError")
  );
}

function matchPattern(rawMessage: string): string | null {
  for (const { pattern, errorCode } of BACKEND_STRING_PATTERNS) {
    if (typeof pattern === "string") {
      if (rawMessage.includes(pattern)) return errorCode;
    } else if (pattern.test(rawMessage)) {
      return errorCode;
    }
  }
  return null;
}

export function getUserFriendlyError(error: unknown, context: ErrorContext): AppError {
  const correlationId = generateCorrelationId();
  const rawMessage = extractRawMessage(error);

  if (isStructuredBackendError(error)) {
    const catalog = ERROR_CATALOG[error.error_code];
    return {
      product: context.product,
      domain: catalog?.domain ?? context.domain,
      error_family: (catalog?.error_family ?? error.error_family) as AppError["error_family"],
      error_code: error.error_code,
      user_title: error.user_title,
      user_message: error.user_message,
      user_action: error.user_action ?? catalog?.user_action,
      retry_allowed: error.retry_allowed,
      severity: catalog?.severity ?? "error",
      correlation_id: error.correlation_id ?? correlationId,
      entity_type: context.entity_type,
      entity_id: context.entity_id,
      technical_message: rawMessage,
      context: context.metadata,
    };
  }

  if (isNetworkError(error)) {
    const entry = getCatalogEntry("NET-001");
    return {
      product: context.product,
      domain: "system",
      error_family: "network",
      error_code: "NET-001",
      ...entry,
      correlation_id: correlationId,
      technical_message: rawMessage,
      entity_type: context.entity_type,
      entity_id: context.entity_id,
      context: context.metadata,
    };
  }

  if (isTimeoutError(error)) {
    const entry = getCatalogEntry("NET-002");
    return {
      product: context.product,
      domain: "system",
      error_family: "timeout",
      error_code: "NET-002",
      ...entry,
      correlation_id: correlationId,
      technical_message: rawMessage,
      entity_type: context.entity_type,
      entity_id: context.entity_id,
      context: context.metadata,
    };
  }

  const matchedCode = matchPattern(rawMessage);
  if (matchedCode) {
    const entry = getCatalogEntry(matchedCode);
    return {
      product: context.product,
      domain: entry.domain,
      error_family: entry.error_family,
      error_code: matchedCode,
      ...entry,
      correlation_id: correlationId,
      technical_message: rawMessage,
      entity_type: context.entity_type,
      entity_id: context.entity_id,
      context: context.metadata,
    };
  }

  const fallback = getCatalogEntry("SYS-001");
  return {
    product: context.product,
    domain: context.domain,
    error_family: "unexpected",
    error_code: "SYS-001",
    ...fallback,
    correlation_id: correlationId,
    technical_message: rawMessage,
    entity_type: context.entity_type,
    entity_id: context.entity_id,
    context: context.metadata,
  };
}
